/**
 * client/input/look.ts — stick view rotation: response curve, minor-axis suppression, turn rates,
 * turn acceleration (pure, DOM-free, unit-tested).
 *
 * OWNER: INPUT agent.
 * Port of T5 aim_assist.cpp AimAssist_CalcAdjustedAxis + AimAssist_ApplyTurnRates
 * (research/controller.md §4.3, MED: algorithm from the BO1 decompile; T6 has the same dvars).
 * Constants are T6 PC dvar defaults [CTL §4.2] (shared/constants.ts).
 */
import {
  AIM_ACCEL_TURNRATE_LERP, AIM_TURNRATE_PITCH, AIM_TURNRATE_PITCH_ADS, AIM_TURNRATE_YAW, AIM_TURNRATE_YAW_ADS,
  CG_FOV, VIEW_SENSITIVITY_TABLE,
} from '@shared/constants';
import { DEG2RAD } from '@shared/math';
import { VIEW_INPUT_GRAPHS } from './controller-data.generated';
import { graphValue } from './stick';

/** aim_input_graph_index 3 (DVAR site 0x701eec). */
export const AIM_INPUT_GRAPH_INDEX = 3;
/** tan(51.08/2): cg_fov 65 at 4:3 as a vertical half-angle tangent (T5 AimAssist_FovScale literal 0.47780272). */
export const TAN_HALF_BASE_FOV_Y = 0.47780272;
/** IW EQUAL_EPSILON used by LinearTrack/DiffTrack. */
const EQUAL_EPSILON = 0.001;

/** input_viewSensitivity for slot 1..14 [CTL §4.1] (clamped; default slot 4 = 1.0). */
export function sensitivityForSlot(slot: number): number {
  const i = Math.min(14, Math.max(1, Math.round(slot))) - 1;
  return VIEW_SENSITIVITY_TABLE[i];
}

/** fovTurnRateScale = tanHalfFovY / 0.47780272 (T5 AimAssist_FovScale). */
export function fovTurnRateScale(fovYDeg: number): number {
  return Math.tan(fovYDeg * 0.5 * DEG2RAD) / TAN_HALF_BASE_FOV_Y;
}

/** fovScaleInv = tan(cg_fov/2)*0.75 / tanHalfFovY (grows when zoomed; autoaim/automelee regions). */
export function fovScaleInv(fovYDeg: number, cgFovDeg = CG_FOV): number {
  return (Math.tan(cgFovDeg * 0.5 * DEG2RAD) * 0.75) / Math.tan(fovYDeg * 0.5 * DEG2RAD);
}

/** Vertical FOV (deg) for a CoD horizontal-at-4:3 fov (cg_fov / adsZoomFov). */
export function vfovFromCodFov(fovDeg: number): number {
  return (2 * Math.atan(Math.tan(fovDeg * 0.5 * DEG2RAD) * 0.75)) / DEG2RAD;
}

/** LinearTrack (IW com_math): step toward tgt at rate*dt, snapping when within a step. */
export function linearTrack(tgt: number, cur: number, rate: number, dt: number): number {
  const err = tgt - cur;
  const step = err <= 0 ? -rate * dt : rate * dt;
  if (Math.abs(err) <= EQUAL_EPSILON) return tgt;
  if (Math.abs(step) > Math.abs(err)) return tgt;
  return cur + step;
}

/** DiffTrack (IW com_math): exponential approach, step = rate*err*dt. */
export function diffTrack(tgt: number, cur: number, rate: number, dt: number): number {
  const err = tgt - cur;
  const step = rate * err * dt;
  if (Math.abs(err) <= EQUAL_EPSILON) return tgt;
  if (Math.abs(step) > Math.abs(err)) return tgt;
  return cur + step;
}

/** DiffTrackAngle (IW com_math): shortest arc, result normalized to [-180,180). */
export function diffTrackAngle(tgt: number, cur: number, rate: number, dt: number): number {
  while (tgt - cur > 180) tgt -= 360;
  while (tgt - cur < -180) tgt += 360;
  const a = diffTrack(tgt, cur, rate, dt) * (1 / 360);
  return (a - Math.floor(a + 0.5)) * 360;
}

export function angleNormalize180(a: number): number {
  const v = a * (1 / 360);
  return (v - Math.floor(v + 0.5)) * 360;
}

/**
 * AimAssist_CalcAdjustedAxis: response graph + minor-axis suppression. Inputs are the axes as
 * CL_GamepadMove hands them over (pitchAxis already sign-flipped for invert, yawAxis = -VA_YAW).
 * altCurve = the CTL §4.3 caveat's alternative reading (output = dir * graph(|v|)), debug A/B.
 */
export function calcAdjustedAxis(out: [number, number], pitchAxis: number, yawAxis: number, altCurve: boolean, graphIndex = AIM_INPUT_GRAPH_INDEX): [number, number] {
  // aim_input_graph_enabled = 1
  const deflection = Math.sqrt(pitchAxis * pitchAxis + yawAxis * yawAxis);
  const fraction = deflection < 0 ? 0 : deflection > 1 ? 1 : deflection;
  const g = graphValue(VIEW_INPUT_GRAPHS[graphIndex] ?? VIEW_INPUT_GRAPHS[AIM_INPUT_GRAPH_INDEX], fraction);
  let p: number, y: number;
  if (altCurve) {
    // ESTIMATE/debug: graph is the output magnitude (direction * graph(|v|))
    const inv = deflection > 0 ? 1 / deflection : 0;
    p = pitchAxis * inv * g; y = yawAxis * inv * g;
  } else {
    // decomp reading [CTL §4.3]: axis * graph(|v|)
    p = pitchAxis * g; y = yawAxis * g;
  }
  // aim_scale_view_axis = 1: suppress the minor axis
  const ap = Math.min(1, Math.abs(p)), ay = Math.min(1, Math.abs(y));
  if (ap <= ay) p *= 1 - (ay - ap);
  else y *= 1 - (ap - ay);
  out[0] = p; out[1] = y;
  return out;
}

export interface TurnRateInput {
  /** -1..1, + = pitch increases (look down). */
  pitchAxis: number;
  /** -1..1, + = yaw increases (turn left). */
  yawAxis: number;
  /** ps.adsFrac (AimAssist_UpdateAdsLerp). */
  adsLerp: number;
  /** fovTurnRateScale(current vertical fov). */
  fovScale: number;
  /** input_viewSensitivity value (not the slot). */
  sensitivity: number;
  /** Slowdown scales from AimAssist_CalcSlowdown (1 = none). */
  slowPitch: number;
  slowYaw: number;
  /** Seconds. */
  dt: number;
  /** Auto-melee is TARGETING: stick ignored (axes zeroed, slowdown 1). */
  autoMeleeTargeting: boolean;
  altCurve: boolean;
}

/**
 * AimAssist_ApplyTurnRates with aim_accel_turnrate_enabled: per-axis speed ramps UP linearly at
 * 1200*sens deg/s^2 and drops instantly (release = instant stop) [CTL §4.3].
 */
export class TurnRateState {
  /** Current angular speeds, deg/s (aaGlob->pitchDelta / yawDelta). */
  pitchSpeed = 0;
  yawSpeed = 0;
  private readonly adj: [number, number] = [0, 0];

  reset(): void { this.pitchSpeed = 0; this.yawSpeed = 0; }

  /** Returns view deltas (deg) in out = [dPitch, dYaw]. */
  apply(out: [number, number], i: TurnRateInput): [number, number] {
    let ap = 0, ay = 0, slowP = i.slowPitch, slowY = i.slowYaw;
    if (i.autoMeleeTargeting) { slowP = 1; slowY = 1; } else { calcAdjustedAxis(this.adj, i.pitchAxis, i.yawAxis, i.altCurve); ap = this.adj[0]; ay = this.adj[1]; }
    const sens = i.sensitivity < 0.001 ? 1 : i.sensitivity; // AimAssist_GetProfileSensitivity
    const ads = i.adsLerp < 0 ? 0 : i.adsLerp > 1 ? 1 : i.adsLerp;
    const pitchRate = slowP * i.fovScale * sens * (AIM_TURNRATE_PITCH + (AIM_TURNRATE_PITCH_ADS - AIM_TURNRATE_PITCH) * ads);
    const yawRate = slowY * i.fovScale * sens * (AIM_TURNRATE_YAW + (AIM_TURNRATE_YAW_ADS - AIM_TURNRATE_YAW) * ads);
    const pitchSign = ap >= 0 ? 1 : -1;
    const yawSign = ay >= 0 ? 1 : -1;
    const pitchTarget = Math.abs(ap) * pitchRate;
    const yawTarget = Math.abs(ay) * yawRate;
    const accel = AIM_ACCEL_TURNRATE_LERP * sens;
    this.pitchSpeed = pitchTarget <= this.pitchSpeed ? pitchTarget : linearTrack(pitchTarget, this.pitchSpeed, accel, i.dt);
    this.yawSpeed = yawTarget <= this.yawSpeed ? yawTarget : linearTrack(yawTarget, this.yawSpeed, accel, i.dt);
    out[0] = this.pitchSpeed * i.dt * pitchSign;
    out[1] = this.yawSpeed * i.dt * yawSign;
    return out;
  }
}
